import CloseIcon from '@mui/icons-material/Close';
import { Box, Fade, IconButton, Popper, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { AnimeatableIconRef } from '@parkdna/ui/icons/types';
import { useTheme } from '@parkdna/ui/theme';
import type { Instance } from '@popperjs/core';
import type { LucideIcon } from 'lucide-react';
import { ArrowUpRight } from 'lucide-react';
import type {
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MenuItem } from 'react-pro-sidebar';

import { useSidebarStore } from '@/stores/sidebar';

import MenuIcon, { type MenuIconRef } from './MenuIcon';

type AnimatedIconComponent = ForwardRefExoticComponent<
  { size?: number } & RefAttributes<AnimeatableIconRef>
>;

type IconComponent = AnimatedIconComponent | LucideIcon;

export type SidebarExternalLinkItemProps = {
  title: string;
  href: string;
  iconComponent?: IconComponent;
  dataCy?: string;
  dataTestId?: string;
  hint?: {
    message: ReactNode;
    storageKey: string;
  };
};

export default function SidebarExternalLinkItem({
  title,
  href,
  iconComponent: Icon,
  dataCy,
  dataTestId,
  hint,
}: SidebarExternalLinkItemProps) {
  const [theme] = useTheme();
  const isSidebarCollapsed = useSidebarStore((state) => state.isCollapsed);
  const isSidebarOpen = useSidebarStore((state) => state.isOpen);
  const isOverlay = useMediaQuery(theme.breakpoints.down('md'));
  const menuIconRef = useRef<MenuIconRef>(null);
  const popperRef = useRef<Instance>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
  const [isHintOpen, setIsHintOpen] = useState(() => {
    if (!hint || typeof window === 'undefined') return false;
    return window.localStorage.getItem(hint.storageKey) !== 'true';
  });

  useEffect(() => {
    if (!isHintOpen) return;
    const rafId = window.requestAnimationFrame(() => {
      void popperRef.current?.update();
    });
    const timeoutId = window.setTimeout(() => {
      void popperRef.current?.update();
    }, 350);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [isHintOpen, isSidebarCollapsed, isOverlay, isSidebarOpen]);

  const shouldShowTooltip = isSidebarCollapsed && !isOverlay;

  const handleMouseEnter = useCallback(() => {
    menuIconRef.current?.startAnimation();
  }, []);

  const handleMouseLeave = useCallback(() => {
    menuIconRef.current?.stopAnimation();
  }, []);

  const handleDismissHint = useCallback(() => {
    if (hint && typeof window !== 'undefined') {
      window.localStorage.setItem(hint.storageKey, 'true');
    }
    setIsHintOpen(false);
  }, [hint]);

  const id = isHintOpen
    ? `${hint?.storageKey ?? 'sidebar-hint'}-popper`
    : undefined;

  return (
    <>
      <MenuItem
        ref={setAnchorEl}
        rootStyles={{
          '& .ps-menu-button': {
            color: theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            transition: theme.transitions.create(
              ['background-color', 'color'],
              { duration: theme.transitions.duration.shorter }
            ),
            '&:hover .external-link-indicator': {
              transform: 'translate(2px, -2px)',
              color: theme.palette.primary.main,
            },
          },
        }}
        component={
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${title} (opens in a new tab)`}
            aria-describedby={id}
          />
        }
        icon={
          Icon ? (
            <MenuIcon
              ref={menuIconRef}
              Icon={Icon}
              title={title}
              shouldShowTooltip={shouldShowTooltip}
              size={18}
            />
          ) : null
        }
        data-cy={dataCy}
        data-testid={dataTestId}
        title={isSidebarCollapsed ? title : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Typography
            variant="h5"
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              verticalAlign: 'middle',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textTransform: 'none',
              flex: 1,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
          <Box
            className="external-link-indicator"
            aria-hidden
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              color: theme.palette.text.secondary,
              bgcolor: theme.palette.background.darker,
              flexShrink: 0,
              transition: theme.transitions.create(['transform', 'color'], {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeOut,
              }),
            }}
          >
            <ArrowUpRight size={14} strokeWidth={2.25} />
          </Box>
        </Box>
      </MenuItem>
      {hint && (
        <Popper
          id={id}
          open={isHintOpen}
          anchorEl={anchorEl}
          popperRef={popperRef}
          placement="right"
          disablePortal={false}
          transition
          modifiers={[
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
          ]}
          sx={{ zIndex: theme.zIndex.tooltip }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={180}>
              <Box>
                <Box
                  ref={setArrowRef}
                  sx={{
                    position: 'absolute',
                    width: 18,
                    height: 18,
                    '&::before': {
                      content: '""',
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'primary.main',
                      transform: 'rotate(45deg)',
                      transformOrigin: 'center',
                    },
                  }}
                />
                <Box
                  role="dialog"
                  aria-label={`${title} hint`}
                  sx={{
                    position: 'relative',
                    maxWidth: 240,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'primary.main',
                    borderRadius: 1.5,
                    boxShadow: 2,
                    px: 1.5,
                    py: 1.25,
                    pr: 4,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      mr: 0.75,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 0.75,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      lineHeight: 1.4,
                      verticalAlign: 'middle',
                    }}
                  >
                    New
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'inline', lineHeight: 1.4 }}
                  >
                    {hint.message}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Dismiss hint"
                    onClick={handleDismissHint}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      p: 0.25,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            </Fade>
          )}
        </Popper>
      )}
    </>
  );
}
